/**
 * Cover (fabric/finish) swatch resolution for master products — shared by PLP tiles,
 * Contentful-driven surfaces, and Shop the look.
 *
 * Mirrors `RangeProductTile`: view-type fallback chain so catalogs without dedicated
 * `swatch` viewTypes still get a usable chip from `large` / `hi-res`.
 */

import {filterImageGroups} from '@salesforce/retail-react-app/app/utils/product-utils';

export const COVER_SWATCH_ATTRIBUTE_ID = 'cover';

const SWATCH_VIEWTYPE_FALLBACK = ['swatch', 'large', 'hi-res'];

/**
 * @param {object} product
 * @param {{[attributeId: string]: string}} variationValues
 * @returns {object|undefined} matched image (`{disBaseLink, link, alt, …}`) or undefined.
 */
export function resolveSwatchImage(product, variationValues) {
  const groups = product?.imageGroups;

  if (!Array.isArray(groups)) return undefined;

  for (const viewType of SWATCH_VIEWTYPE_FALLBACK) {
    const group = filterImageGroups(groups, {viewType, variationValues})?.[0];

    if (group?.images?.length > 0) {
      return group.images[0];
    }
  }

  for (const viewType of SWATCH_VIEWTYPE_FALLBACK) {
    const group = groups.find(
      (g) => g?.viewType === viewType && !g.variationAttributes && g.images?.length > 0
    );

    if (group) return group.images[0];
  }

  return undefined;
}

/**
 * @param {object} product
 * @returns {Array<{name?: string, value?: string, swatch?: object, orderable?: boolean}>}
 */
export function getCoverSwatches(product) {
  const variationAttributes = product?.variationAttributes;

  if (!Array.isArray(variationAttributes)) return [];

  const coverAttr = variationAttributes.find(({id}) => id === COVER_SWATCH_ATTRIBUTE_ID);

  if (!coverAttr?.values) return [];

  return coverAttr.values.map((value) => ({
    ...value,
    swatch: resolveSwatchImage(product, {[coverAttr.id]: value.value}),
  }));
}

/**
 * One cover line for compact UIs (e.g. Shop the look drawer): prefers the first orderable
 * variant's `cover`, then the first orderable cover value with an image, then any with an image,
 * else the first cover value.
 *
 * @param {object|null|undefined} product
 * @returns {{label: string, swatchUrl: string|undefined}|null}
 */
export function getPrimaryCoverSwatchDisplay(product) {
  const coverAttr = product?.variationAttributes?.find(({id}) => id === COVER_SWATCH_ATTRIBUTE_ID);

  if (!coverAttr?.values?.length) return null;

  const {values} = coverAttr;
  const attrId = coverAttr.id;
  let picked = null;

  const variants = product?.variants;

  if (Array.isArray(variants) && variants.length > 0) {
    const v = variants.find((x) => x?.orderable !== false) ?? variants[0];
    const cv = v?.variationValues?.cover;

    if (cv != null && cv !== '') {
      picked = values.find((x) => x.value === cv) ?? null;
    }
  }

  if (!picked) {
    picked =
      values.find((x) => x.orderable !== false && resolveSwatchImage(product, {[attrId]: x.value})) ??
      values.find((x) => resolveSwatchImage(product, {[attrId]: x.value})) ??
      values[0];
  }

  if (!picked) return null;

  const img = resolveSwatchImage(product, {[attrId]: picked.value});
  const swatchUrl = img?.disBaseLink || img?.link;
  const label = (picked.name != null && String(picked.name).trim() !== '' ? picked.name : null) ??
    (picked.value != null ? String(picked.value) : '');

  if (!label && !swatchUrl) return null;

  return {label, swatchUrl: swatchUrl || undefined};
}
